import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { CORE, SALES } from '@/constants/team_v4';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores/FlashesStore';
import { initStore, pollStore } from '@/stores/store-utils';
import {
  BulkRejectSeatRequestsPayload,
  MAX_BULK_REJECT_SEAT_REQUESTS,
  SeatRequest,
  SeatRequestStatus,
} from '@/types/seat_requests';

export const useSeatRequestsStore = defineStore('SeatRequests', () => {
  const flashesStore = useFlashesStore();
  const seatRequests = ref<SeatRequest[]>([]);

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const { data, error } = await crossbeamApi.GET('/v0.1/seat-requests');
      if (error) throw error;
      seatRequests.value =
        data?.filter(({ status }) => status === SeatRequestStatus.pending) ??
        [];
    },
  );

  refresh({ useCache: true });

  const fullAccessRequests = computed(() =>
    seatRequests.value.filter(({ type }) => type === CORE),
  );
  const salesAccessRequests = computed(() =>
    seatRequests.value.filter(({ type }) => type === SALES),
  );

  function getSeatRequestByEmail(email: string) {
    return seatRequests.value.find((request) => request.email === email);
  }

  function getSeatRequestByUuid(uuid: string) {
    return seatRequests.value.find((request) => request.id === uuid);
  }

  async function bulkRejectSeatRequests(
    ids: BulkRejectSeatRequestsPayload['ids'],
  ): Promise<boolean> {
    const count = ids.length;
    if (!count) return false;
    if (count > MAX_BULK_REJECT_SEAT_REQUESTS) {
      flashesStore.addErrorFlash({
        message: `You can only reject up to ${MAX_BULK_REJECT_SEAT_REQUESTS} requests at a time`,
      });
      return false;
    }
    const { error } = await crossbeamApi.PATCH('/v0.1/seat-requests-bulk', {
      body: { ids },
    });

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({
        message: `Could not reject ${count} seat ${count === 1 ? 'request' : 'requests'}`,
      });
      return false;
    }

    const rejectedIds = new Set(ids);
    seatRequests.value = seatRequests.value.filter(
      ({ id }) => !rejectedIds.has(id),
    );
    flashesStore.addSuccessFlash({
      message: `${count} seat ${count === 1 ? 'request' : 'requests'} rejected`,
    });
    return true;
  }

  /* Poll store after it is initialized */
  const { startPolling, stopPolling, isPolling } = pollStore(
    readySync,
    refresh,
  );

  return {
    error: initError,
    ready,
    readySync,
    refreshSeatRequestsStore: refresh,
    startPolling,
    stopPolling,
    isPolling,
    bulkRejectSeatRequests,
    seatRequests,
    running,
    fullAccessRequests,
    salesAccessRequests,
    getSeatRequestByEmail,
    getSeatRequestByUuid,
  };
});
